import { HttpClient, HttpErrorResponse, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError, timer } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Product } from '../component/pages/dashboard/product.store';
import { Email, Generate, Progress, Template } from '../component/pages/generator/email.store';
import { MetaCategory } from '../model/meta-category.model';
import { catchError, map, retry } from 'rxjs/operators';

type Images = { [key: string]: string };
interface GifResponse {
  task_id: string;
}

export interface BestMatchAndPrompt {
  best_match: [number, number];
  prompt: string;
}

export interface GifStatus {
  status: 'RUNNING' | 'SUCCEEDED' | 'FAILED';
  phase?: 'VIDEO_GENERATING' | 'GIF_RENDERING' | 'SUCCEEDED' | 'FAILED';
  progress?: number;      // vidéo: 0..100 (ou 0..1 si tu gardes l’existant)
  gif_progress?: number;  // gif: 0..100
  gif_url?: string;
  gif_urls?: string[];
  sizes?: string[];

  error?: string;
  message?: string;
  support_code?: string;
}

export interface AmbianceStatus {
  status: 'RUNNING' | 'SUCCEEDED' | 'FAILED';
  image_url?: string;   // présent quand status === 'SUCCEEDED'
}

export interface StripoTag {
  id: number;
  name: string;
}

export interface StripoTemplate {
  templateId: number;
  name: string;
  logo: string;
  premium: boolean;
  hasAmp: boolean;
  updatedAt: number;
  createdTime: number;
  templateTypes?: StripoTag[];
  templateSeasons?: StripoTag[];
  templateIndustries?: StripoTag[];
  templateFeatures?: StripoTag[];
}

export interface StripoListResponse {
  data: StripoTemplate[];
  total: number;
}

/** Reponse de /api/write_cta_on_image/ : le CTA seul, une URL par variante. */
export interface CtaOnImageResponse {
  statut: string;
  cta_image_solid: string;
  cta_image_rounded: string;
  cta_image_outline: string;
  cta_width: number;
  cta_height: number;
  background_color: string;
  text_color: string;
  resized_original_url: string;
}

@Injectable({ providedIn: 'root' })
export class GeneratorService {
  constructor(private httpClient: HttpClient) { }

  public getTemplates(): Observable<Template[]> {
    return this.httpClient.get<Template[]>(`${environment.backendUrl}/api/template`);
  }

  public updateTemplate(id: number, data: Partial<Template>): Observable<Template> {
    return this.httpClient.patch<Template>(`${environment.backendUrl}/api/template/${id}/`, data);
  }

  /** Attention : la pagination Stripo est indexée à 0, la première page est page=0. */
  public getStripoTemplates(
    type: 'FREE' | 'BASIC' | 'PREMIUM',
    sort: 'ACTUAL' | 'NEW' = 'ACTUAL',
    limit?: number,
    page?: number
  ): Observable<StripoListResponse> {
    let params = new HttpParams()
      .set('type', type)
      .set('sort', sort);

    if (limit != null) { params = params.set('limit', limit.toString()); }
    if (page != null) { params = params.set('page', page.toString()); }

    return this.httpClient.get<StripoListResponse>(
      `${environment.backendUrl}/api/stripo/templates/`,
      { params }
    );
  }

  /** Catalogue Inspiration complet (gratuits + premium), mis en cache côté Django. */
  public getStripoInspirationCatalog(): Observable<StripoListResponse> {
    return this.httpClient.get<StripoListResponse>(
      `${environment.backendUrl}/api/stripo/templates/catalog/`
    );
  }

  createEmailFromTemplate(templateId: number, name: string, type: string, folder_id?: number | null): Observable<{ message: string; id: number }> {
    return this.httpClient.post<{ message: string; id: number }>(
      `${environment.backendUrl}/api/template/`,
      { id_template: templateId, name: name, type: type, folder_id: folder_id }
    );
  }

  public generateEmail(
    metaCategoryId: MetaCategory['id'],
    ids: Product['product_id'][],
    images_products_urls: Product['image_url_1'][],
    theme: string,
    templateId: Template['id'],
    visuel_url?: string,
    task_id?: string,
    selected_ratio?: number,
    wantGif?: boolean,
    selectedLogo?: string,
    apply_discount?: boolean,
    apply_global_link?: boolean,
    global_link_url?: string,
  ): Observable<Generate> {
    const payload: any = {
      meta: metaCategoryId,
      ids,
      images_products_urls,
      theme,
      id_template: templateId,
      apply_discount: apply_discount,
      apply_global_link: apply_global_link ?? false,
      global_link_url: global_link_url ?? '',
    };

    if (visuel_url) {
      payload.visuel_url = visuel_url;
    }

    if (task_id) {
      payload.task_id = task_id;
    }
    if (selected_ratio) {
      payload.selected_ratio = selected_ratio;
    }
    if (wantGif) {
      payload.wantGif = wantGif;
    }
    if (selectedLogo) {
      payload.selected_logo = selectedLogo;
    }

    return this.httpClient.post<Generate>(`${environment.backendUrl}/api/generate/`, payload);
  }

  public generateEmailFinal(
    task_id: Generate['task_id'],
    images: Images,
  ): Observable<Generate> {
    return this.httpClient.post<Generate>(`${environment.backendUrl}/api/generate_final/`, {
      task_id,
      images,
    });
  }

  public progress(id: Generate['task_id']): Observable<Progress> {
    return this.httpClient.get<Progress>(`${environment.backendUrl}/api/progress/${id}`);
  }

  public getEmails(): Observable<Email[]> {
    return this.httpClient.get<Email[]>(`${environment.backendUrl}/api/generate/`);
  }

  public getEmail(id: Email['id']): Observable<Email> {
    return this.httpClient.get<Email>(`${environment.backendUrl}/api/generate/`, {
      params: { id_generated: `${id}` },
    });
  }

  public saveEmail(id: Email['id'] | null, html: string, css: string, name: string, isExporting: boolean, folder_id: number | null, isManualSave: boolean): Observable<string> {
    return this.httpClient.post<string>(`${environment.backendUrl}/api/generatesave/`, {
      id,
      html,
      css,
      name,
      isExporting,
      folder_id,
      isManualSave
    });
  }

  public createEmptyEmail(folder_id?: number | null): Observable<{ message: string; id: number }> {
    let params = {};

    // Add folder_id to params if provided
    if (folder_id !== undefined && folder_id !== null) {
      params = { folder_id: folder_id };
    }

    return this.httpClient.post<{ message: string; id: number }>(
      `${environment.backendUrl}/api/template/`,
      params
    );
  }


  public getLangagecode(get_language_code: number | null): Observable<string> {
    return this.httpClient.post<string>(`${environment.backendUrl}/api/generatesave/`, {
      get_language_code
    });
  }
  public translateEmail(id: Email['id'], langage_code: string): Observable<Email> {
    return this.httpClient.post<Email>(`${environment.backendUrl}/api/generatesave/`, {
      id,
      langage_code,
    });
  }
  public createInterEmail(id: Email['id']): Observable<Email> {
    return this.httpClient.post<Email>(`${environment.backendUrl}/api/generatesave/`, {
      id,
      inter: 1,
    });
  }
  public copyEmail(id: Email['id'], html: string | undefined, css: string | undefined): Observable<Email> {
    return this.httpClient.post<Email>(`${environment.backendUrl}/api/generatesave/`, {
      id,
      html,
      css,
      copy: 1  // Indiquer que c'est une copie
    });
  }
  public sendBat(subject: string, html_b64: string, to_list: string[]) {
    return this.httpClient.post(`${environment.backendUrl}/api/send_bat/`, {
      objet: subject,
      html_b64: html_b64,
      adresses_emails: to_list,
    });
  }



  public createDynamicEmail(
    id: Email['id'],
    html: string | undefined,
    css: string | undefined,
    dynamic_category: number,
    campaign_type: 'one_shot' | 'automation' = 'one_shot',
    force: boolean = false,
  ): Observable<{ task_id: string; status: string; message: string }> {
    return this.httpClient.post<{ task_id: string; status: string; message: string }>(
      `${environment.backendUrl}/api/generatesave/`,
      {
        id,
        html,
        css,
        dynamic: 1,
        dynamic_category: dynamic_category,
        campaign_type: campaign_type,
        force,
      }
    );
  }



  public resizeImageRatio(url: string, ratio: number, prompt: string): Observable<{ [key: string]: string }> {
    // Créer les paramètres de requête
    let params = new HttpParams()
      .set('url', url)
      .set('ratio', ratio.toString())
      .set('prompt', prompt);

    // Envoyer la requête avec les paramètres
    // Assurez-vous que le type de retour correspond à la nouvelle structure de réponse
    return this.httpClient.post<{ [key: string]: string }>(`${environment.backendUrl}/api/image_resize_ratio/`, null, {
      params: params,
    });
  }
  /**
   * Génère le bouton CTA à poser sur le visuel (PNG transparent haute
   * définition) ainsi que le visuel redimensionné servant de fond. La fusion se
   * fait ensuite via mergeImages().
   */
  public WriteCTAOnImage(
    image_url: string,
    text: string,
    background_color?: string
  ): Observable<CtaOnImageResponse> {
    const body: { [key: string]: string } = {
      text: text,
      image_url: image_url,
    };

    if (background_color) {
      body['background_color'] = background_color;
    }

    return this.httpClient.post<CtaOnImageResponse>(`${environment.backendUrl}/api/write_cta_on_image/`, body);
  }

  public majRemiseProduct(
    image_url: string,
    html_url: string,
    width: string,
    remise_bg_color?: string,
    remise_text?: string
  ): Observable<string> {
    const body: any = {
      url_html: html_url,
      url_image: image_url,
      width: width.toString(),
    };

    // Only include if provided (already sanitized to #RRGGBB)
    if (remise_bg_color) {
      body.remise_bg_color = remise_bg_color;
    }

    // Optional custom display text overriding the scraped discount
    if (remise_text) {
      body.remise_text = remise_text;
    }

    return this.httpClient.post<string>(
      `${environment.backendUrl}/api/maj_remise_produit/`,
      body
    );
  }

  public addModelProduct(image_url: string, text: string, width_block: string, color?: string, underline?: boolean, enterprise_id?: number, font_size?: number): Observable<string> {
    const body: any = {
      image_url,
      text,
      width_block,
    };
    if (color) body.color = color;
    if (underline !== undefined) body.underline = underline;
    if (enterprise_id != null) body.enterprise_id = enterprise_id;
    if (font_size != null) body.font_size = font_size;
    return this.httpClient.post<string>(`${environment.backendUrl}/api/add_model_product/`, body);
  }

  public addLogoOnImage(image_url: string, html_url: string, width: string): Observable< string > {
    const body = {
      url_image: image_url,
      url_html: html_url,
      width: width.toString(),
    };

    return this.httpClient.post<string>(
      `${environment.backendUrl}/api/ajout_logo_on_image/`,
      body
    );
  }

  public recadrageProduct(url_image: string, width_block: string, height_block: string): Observable<{ [key: string]: string }> {
    const body = {
      url_image: url_image,
      width_block: width_block.toString(),
      height_block: height_block.toString(),
    };

    return this.httpClient.post<{ [key: string]: string }>(`${environment.backendUrl}/api/recadrage_produit/`, body);
  }

  public removeBackgroud(url_image: string, bgColor: string | null): Observable<{ [key: string]: string }> {
    const body = {
      url_image: url_image,
      bg_color: bgColor
    };

    return this.httpClient.post<{ [key: string]: string }>(`${environment.backendUrl}/api/remove_backgroud/`, body);
  }

  public bestMatchAndPrompt(url: string): Observable<BestMatchAndPrompt> {
    // Envoyer les paramètres dans le corps de la requête POST
    const body = { url_image: url };
    return this.httpClient.post<BestMatchAndPrompt>(`${environment.backendUrl}/api/best_match_and_prompt/`, body);
  }

  public imageToGif(url: string, best_match: [number, number], prompt: string, animationMode: string, width_block: number): Observable<GifResponse> {
    // Envoyer les paramètres dans le corps de la requête POST
    const body = {
      image_url: url,
      best_match: best_match,
      prompt: prompt,
      animation_mode: animationMode,
      width_block: width_block
    };
    return this.httpClient.post<GifResponse>(`${environment.backendUrl}/api/create_gif_from_static_image/`, body)
      .pipe(
        // If the server ever returned 4xx here, bubble it up
        catchError((err: HttpErrorResponse) => throwError(() => err))
      );

  }

  public imageToGifProgress(task_id: string): Observable<GifStatus> {
    return this.httpClient.get<GifStatus>(`${environment.backendUrl}/api/create_gif_from_static_image/${task_id}/`)
      .pipe(
        map(resp => resp),
        // erreur passagère (déploiement, 502/504, réseau) : on retente 2 fois avant d'abandonner, sauf 422 (filtre IA)
        retry({ count: 2, delay: (err: HttpErrorResponse) => err.status === 422 ? throwError(() => err) : timer(2000) }),
        // ← turn 422 (and other errors) into a FAILED GifStatus with details
        catchError((err: HttpErrorResponse) => {
          if (err.status === 422) {
            const body = err.error || {};
            return of({
              status: 'FAILED',
              error: body.error ?? 'rai_filtered',
              message: body.message ?? 'Contenu bloqué par le filtre de sécurité.',
              support_code: body.support_code
            } as GifStatus);
          }
          // other statuses → generic failure
          return of({
            status: 'FAILED',
            error: 'http_error',
            message: (err.error?.message as string) || 'Erreur réseau / serveur.'
          } as GifStatus);
        })
      );
  }

  /** Mise en ambiance : 1 à 5 vues du produit (la 1re = image de l'email), l'ambiance vient d'un fichier ou d'une URL. */
  public ambianceImage(imageUrls: string[], ambiance: File | string, keepProductAngle: boolean, ratio?: number): Observable<GifResponse> {
    const body = new FormData();
    imageUrls.forEach(url => body.append('image_urls', url));
    body.append('keep_product_angle', String(keepProductAngle));
    if (ratio) body.append('ratio', String(ratio));
    if (typeof ambiance === 'string') {
      body.append('ambiance_url', ambiance);
    } else {
      body.append('ambiance_file', ambiance);
    }
    return this.httpClient.post<GifResponse>(`${environment.backendUrl}/api/ambiance_image/`, body);
  }

  public ambianceImageProgress(task_id: string): Observable<AmbianceStatus> {
    return this.httpClient.get<AmbianceStatus>(`${environment.backendUrl}/api/ambiance_image/${task_id}/`)
      .pipe(
        // erreur passagère (déploiement, 502/504, réseau) : on retente 2 fois avant d'abandonner
        retry({ count: 2, delay: () => timer(2000) })
      );
  }


  public writeOnImage(url: string, text_up: string, text_down: string, single_text: string, text_color?: string): Observable<{ [key: string]: string }> {
    // Créer les paramètres de requête
    let params = new HttpParams()
      .set('url', url)
      .set('text_up', text_up)
      .set('text_down', text_down)
      .set('single_text', single_text);

    if (text_color) {
      params = params.set('text_color', text_color);
    }

    // Envoyer la requête avec les paramètres
    return this.httpClient.post<{ [key: string]: string }>(`${environment.backendUrl}/api/write_on_image/`, null, {
      params: params,
    });
  }

  public deleteEmail(id: Email['id']): Observable<void> {
    return this.httpClient.get<void>(`${environment.backendUrl}/api/generate/`, {
      params: {
        del_email: `${id}`,
      },
    });
  }

  public regenerateImage(taskId: string, imageUrl: string, content: string, task_id_midjourney: string): Observable<any> {
    return this.httpClient.post<any>(`${environment.backendUrl}/api/image_selection/`, {
      reload: true,
      task_id: taskId,
      url_discord: imageUrl,
      content: content,
      task_id_midjourney: task_id_midjourney
    });
  }

  public selectImage(imageNumber: number, imageUrl: string): Observable<any> {
    return this.httpClient.post<any>(`${environment.backendUrl}/api/image_selection/`, {
      reload: false,
      img_number: imageNumber,
      url_discord: imageUrl,
    });
  }

  public uploadFile(file: File): Observable<string> {
    const formData = new FormData();
    const sanitizedFileName = file.name.replace(/[\s()]+/g, '');
    formData.append('file', file, sanitizedFileName);

    // Supposons que vous ayez un endpoint séparé pour l'upload d'images
    return this.httpClient.post<{ url: string }>(`${environment.backendUrl}/api/image_tmp/upload/`, formData).pipe(
      map(response => response.url)
    );
  }

  public exportSocial(imageUrl: string, htmlUrl: string): Observable<Blob> {
    return this.httpClient.post(`${environment.backendUrl}/api/export_social/?url_html_image=${htmlUrl}&url_preview_image=${imageUrl}`, null, {
      responseType: 'blob'
    });
  }

  public resizeImagesInHtml(html: string): Observable<{ html: string }> {
    return this.httpClient.post<{ html: string }>(`${environment.backendUrl}/api/resize_images_in_html/`, { html: html });
  }

  public createGifProduct(selected_images: string[], ratio_produit: number): Observable<any> {
    return this.httpClient.post<any>(`${environment.backendUrl}/api/create_gif_product/`, {
      selected_images: selected_images,
      ratio_produit: ratio_produit
    });
  }

  public scrapImageProductPage(url_image: string, url_html: string): Observable<any> {
    return this.httpClient.post<any>(`${environment.backendUrl}/api/scrap_image_product_page/`, {
      url_image: url_image,
      url_html: url_html
    });
  }

  public getTaskProgress(taskId: string): Observable<any> {
    return this.httpClient.get<any>(`${environment.backendUrl}/api/progress/${taskId}/`);
  }

  public getTaskProgressList(taskType: string): Observable<any[]> {
    return this.httpClient.get<any[]>(`${environment.backendUrl}/api/progress/`, {
      params: { task_type: taskType }
    });
  }

  public addTrackingToUrls(html: string | undefined, tracking: string): Observable<{ html: string }> {
    return this.httpClient.post<{ html: string }>(`${environment.backendUrl}/api/add_tracking_to_urls/`, {
      html: html,
      tracking: tracking
    });
  }

  public pushEmail(email_id: number, html: string): Observable<{ status: string; message: string }> {
    return this.httpClient.post<{ status: string; message: string }>(`${environment.backendUrl}/api/push_email/`, {
      email_id: email_id,
      html_compiled: html
    });
  }

  public changeValueInEnterprise(id_entreprise: number, field: string, value: string): Observable<any> {
    return this.httpClient.patch<any>(`${environment.backendUrl}/api/enterprise/${id_entreprise}/`, {
      [field]: value
    });
  }

  /**
   * Search Adobe Stock images using a search term.
   * @param searchTerm The search term to use for the Adobe Stock API.
   * @returns An Observable containing a list of image URLs.
   */
  public searchAdobeStock(searchTerm: string): Observable<{ urls: string[]; }> {
    const payload = { search_term: searchTerm };

    return this.httpClient.post<{ urls: string[]; }>(
      `${environment.backendUrl}/api/adobe_stock_search/`,
      payload
    );
  }

  public mergeImages(staticImageUrl: string, overlayImageUrl: string, x: number, y: number, width: number, height: number): Observable<{ merged_image_url: string }> {
    return this.httpClient.post<{ merged_image_url: string }>(`${environment.backendUrl}/api/merge_image_overlay/`, {
      static_image_url: staticImageUrl,
      overlay_image_url: overlayImageUrl,
      x: x,
      y: y,
      width: width,
      height: height
    });
  }

  public checkStripoImport(): Observable<boolean> {
    return this.httpClient.get<boolean>(`${environment.backendUrl}/api/import_html_from_stripo_email_id/check_webhook/`);
  }

  public importHtmlFromStripoEmailId(emailId: string, name?: string, folderId?: number | null): Observable<{ id: number; name: string }> {
    const body: any = { email_id: emailId };
    if (name?.trim()) body.name = name.trim();
    if (folderId != null) body.folder_id = folderId;
    return this.httpClient.post<{ id: number; name: string }>(`${environment.backendUrl}/api/import_html_from_stripo_email_id/`, body);
  }

  public checkMajProduitWebhook(): Observable<{ has_webhook: boolean }> {
    return this.httpClient.get<{ has_webhook: boolean }>(`${environment.backendUrl}/api/maj_produit/check_webhook/`);
  }

  public majProduit(payload: any = {}): Observable<{
    task_id: string;
    status: string;
    message: string;
    minutes_since_completion?: number;
  }> {
    return this.httpClient.post<any>(`${environment.backendUrl}/api/maj_produit/`, payload);
  }

  public getDynamicImages(emailId: number): Observable<any[]> {
    return this.httpClient.get<any[]>(`${environment.backendUrl}/api/dynamic_content/list_images/`, {
      params: { email_id: emailId.toString() }
    });
  }

  public swapDynamicImage(emailId: number, productId: string, visibleIds: string[]): Observable<any> {
    return this.httpClient.post<any>(`${environment.backendUrl}/api/dynamic_content/swap_image/`, {
      email_id: emailId,
      product_id: productId,
      visible_ids: visibleIds
    });
  }

  public deleteDynamicImages(emailId: number, productIds: string[]): Observable<any> {
    return this.httpClient.post<any>(`${environment.backendUrl}/api/dynamic_content/delete_images/`, {
      email_id: emailId,
      product_ids: productIds
    });
  }

  public checkExistingDynamicCopy(emailId: number): Observable<any> {
    return this.httpClient.get<any>(`${environment.backendUrl}/api/dynamic_content/check_existing_copy/`, {
      params: { email_id: emailId.toString() }
    });
  }

  public getSampleUsers(emailId: number): Observable<any> {
    return this.httpClient.get<any>(`${environment.backendUrl}/api/dynamic_content/sample_users/`, {
      params: { email_id: emailId.toString() }
    });
  }

  public getPersonalizableSegmentCounts(emailId: number): Observable<{ total: number, segments: { label: string, count: number }[] }> {
    return this.httpClient.get<{ total: number, segments: { label: string, count: number }[] }>(
      `${environment.backendUrl}/api/dynamic_content/personalizable_segment_counts/`, {
      params: { email_id: emailId.toString() }
    });
  }

  public exportPersonalizableUsers(emailId: number, segments: string[] = []): Observable<HttpResponse<Blob>> {
    const params: { [key: string]: string } = { email_id: emailId.toString() };
    if (segments.length > 0) {
      params['segments'] = segments.join(',');
    }
    return this.httpClient.get(`${environment.backendUrl}/api/dynamic_content/export_personalizable_users/`, {
      params,
      responseType: 'blob',
      observe: 'response',
    });
  }

}
